import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, User, Eye, Ban, CheckCircle, Loader, X, AlertTriangle,
  Mail, Phone, MapPin, Calendar, DollarSign, CreditCard, RefreshCw,
  Shield, Clock, TrendingUp, Trash2
} from 'lucide-react';
import adminService, {
  User as UserType, UserTransaction, UserStatus, GetUsersParams
} from '../../services/admin/adminService';
import toast from 'react-hot-toast';
import { getImageUrl } from '../../utils/imageHelper';
import DataTable, { Column } from '../../components/common/DataTable';

const UserManagement: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserType[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [totalUsers, setTotalUsers] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    suspended: 0,
    verified: 0
  });

  // Suspend Modal
  const [suspendModal, setSuspendModal] = useState<{ show: boolean; user: UserType | null }>({
    show: false,
    user: null
  });

  // Form states
  const [suspensionReason, setSuspensionReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [bulkDeleteModal, setBulkDeleteModal] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleSelectUser = (userId: number) => {
    setSelectedUsers(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const handleSelectAllUsers = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(u => u.user_id));
    }
  };

  const handleBulkDelete = async () => {
    try {
      setProcessing(true);
      await adminService.bulkDeleteUsers(selectedUsers);
      toast.success('Selected users deleted successfully!');
      setSelectedUsers([]);
      setBulkDeleteModal(false);
      fetchUsers();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete users');
    } finally {
      setProcessing(false);
    }
  };

  const fetchUsers = async (
    showToast = false,
    pageNum = currentPage,
    limitNum = pageSize,
    status = statusFilter,
    search = searchQuery
  ) => {
    try {
      if (showToast) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const params: GetUsersParams = {
        page: pageNum,
        limit: limitNum,
      };

      if (status && status !== 'all') {
        params.status = status as UserStatus;
      }

      if (search && search.trim()) {
        params.search = search.trim();
      }

      const data = await adminService.getAllUsers(params);
      setUsers(data.users);
      setTotalUsers(data.pagination.total_users);
      if (data.stats) {
        setStats(data.stats);
      } else {
        setStats({
          total: data.pagination.total_users,
          active: data.users.filter(u => u.is_active && !u.is_suspended).length,
          suspended: data.users.filter(u => u.is_suspended).length,
          verified: data.users.filter(u => u.is_verified).length
        });
      }
      setSelectedUsers([]); // Reset selection on reload

      if (showToast) {
        toast.success('Users refreshed!');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    fetchUsers(false, newPage, pageSize, statusFilter, searchQuery);
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
    fetchUsers(false, 1, newSize, statusFilter, searchQuery);
  };

  const handleFilterChange = (filterVal: string) => {
    setStatusFilter(filterVal);
    setCurrentPage(1);
    fetchUsers(false, 1, pageSize, filterVal, searchQuery);
  };

  const handleSearchChange = (term: string) => {
    setSearchQuery(term);
    setCurrentPage(1);
    fetchUsers(false, 1, pageSize, statusFilter, term);
  };


  const handleViewDetails = (userId: number) => {
    navigate(`/admin/users/${userId}`);
  };



  const handleSuspendUser = async () => {
    if (!suspendModal.user || !suspensionReason.trim()) {
      toast.error('Please provide a suspension reason');
      return;
    }

    try {
      setProcessing(true);
      await adminService.suspendUser({
        user_id: suspendModal.user.user_id,
        suspension_reason: suspensionReason
      });
      toast.success('User suspended successfully!');
      setSuspendModal({ show: false, user: null });
      setSuspensionReason('');
      fetchUsers();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to suspend user');
    } finally {
      setProcessing(false);
    }
  };

  const handleUnsuspend = async (userId: number) => {
    try {
      await adminService.unsuspendUser(userId);
      toast.success('User unsuspended successfully!');
      fetchUsers();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to unsuspend user');
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };



  const getStatusBadge = (user: UserType) => {
    if (user.is_suspended) {
      return 'bg-danger-50 text-danger-600 border-danger-200';
    }
    if (user.is_active) {
      return 'bg-success-50 text-success-600 border-success-200';
    }
    return 'bg-secondary-50 text-secondary-600 border-secondary-200';
  };

  const getStatusText = (user: UserType) => {
    if (user.is_suspended) return 'Suspended';
    if (user.is_active) return 'Active';
    return 'Inactive';
  };

  const getTransactionStatusBadge = (status: string) => {
    const styles: any = {
      success: 'bg-success-50 text-success-600',
      pending: 'bg-warning-50 text-warning-600',
      failed: 'bg-danger-50 text-danger-600',
      refunded: 'bg-info-50 text-info-600',
      cancelled: 'bg-secondary-50 text-secondary-600'
    };
    return styles[status] || 'bg-secondary-50 text-secondary-600';
  };

  if (loading && !refreshing) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader className="animate-spin text-primary-500" size={48} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900 mb-2">User Management</h1>
          <p className="text-secondary-600">Manage and monitor platform users</p>
        </div>
        <div className="flex items-center space-x-3">
          {selectedUsers.length > 0 && (
            <button
              onClick={() => setBulkDeleteModal(true)}
              className="bg-danger-500 hover:bg-danger-600 text-white px-4 py-2 rounded-lg font-semibold flex items-center space-x-2 transition-colors animate-fade-in"
            >
              <Trash2 size={18} />
              <span>Delete Selected ({selectedUsers.length})</span>
            </button>
          )}
          <button
            onClick={() => fetchUsers(true)}
            disabled={refreshing}
            className="bg-secondary-50 hover:bg-secondary-100 text-secondary-700 px-4 py-2 rounded-lg font-semibold flex items-center space-x-2 transition-colors disabled:opacity-50"
          >
            <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl p-6 shadow-soft border-l-4 border-primary-500">
          <p className="text-sm text-secondary-600 mb-2">Total Users</p>
          <p className="text-3xl font-bold text-primary-600">{stats.total}</p>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-soft border-l-4 border-success-500">
          <p className="text-sm text-secondary-600 mb-2">Active Users</p>
          <p className="text-3xl font-bold text-success-600">{stats.active}</p>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-soft border-l-4 border-info-500">
          <p className="text-sm text-secondary-600 mb-2">Verified Users</p>
          <p className="text-3xl font-bold text-info-600">{stats.verified}</p>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-soft border-l-4 border-danger-500">
          <p className="text-sm text-secondary-600 mb-2">Suspended</p>
          <p className="text-3xl font-bold text-danger-600">{stats.suspended}</p>
        </div>
      </div>

      {/* Users List */}
      <DataTable
        data={users}
        loading={loading}
        searchable={true}
        searchPlaceholder="Search by name, email, phone..."
        searchFields={['full_name', 'email', 'mobile_number']}
        filterable={true}
        filterOptions={[
          { label: 'Active', value: 'active', field: 'is_active' },
          { label: 'Suspended', value: 'suspended', field: 'is_suspended' },
          { label: 'Inactive', value: 'inactive', field: 'is_active' }
        ]}
        defaultPageSize={pageSize}
        pageSizeOptions={[10, 20, 50, 100]}
        totalCount={totalUsers}
        currentPage={currentPage}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onFilterChange={handleFilterChange}
        onSearchChange={handleSearchChange}
        emptyMessage="No users found"
        emptyIcon={<User size={64} className="text-secondary-300 mx-auto mb-4" />}
        columns={[
          {
            header: (
              <input
                type="checkbox"
                checked={selectedUsers.length === users.length && users.length > 0}
                onChange={handleSelectAllUsers}
                className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500 w-4 h-4 cursor-pointer"
              />
            ),
            accessor: (user) => (
              <input
                type="checkbox"
                checked={selectedUsers.includes(user.user_id)}
                onChange={() => handleSelectUser(user.user_id)}
                className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500 w-4 h-4 cursor-pointer"
                onClick={(e) => e.stopPropagation()}
              />
            ),
            width: '40px',
          },
          {
            header: 'User',
            accessor: (user) => (
              <div className="flex items-center space-x-3">
                {user.profile_image ? (
                  <img
                    src={getImageUrl(user.profile_image)}
                    alt={user.full_name}
                    className="w-12 h-12 rounded-full object-cover"
                    onError={(e) => {
                      e.currentTarget.src = '';
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                    <User className="text-primary-600" size={24} />
                  </div>
                )}
                <div>
                  <p className="font-semibold text-secondary-900">{user.full_name}</p>
                  <div className="flex items-center space-x-2 mt-1">
                    <span className="text-xs text-secondary-500 font-mono">ID: {user.user_id}</span>
                    {user.country && (
                      <span className="bg-secondary-100 text-secondary-600 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider">
                        {user.country}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ),
            sortable: true,
          },
          {
            header: 'Contact',
            accessor: (user) => (
              <div className="space-y-1">
                <p className="text-sm flex items-center space-x-1">
                  <Mail size={12} className="text-secondary-400" />
                  <span className="text-secondary-700">{user.email}</span>
                </p>
                {user.mobile_number && (
                  <p className="text-sm flex items-center space-x-1">
                    <Phone size={12} className="text-secondary-400" />
                    <span className="text-secondary-700">{user.mobile_number}</span>
                  </p>
                )}
              </div>
            ),
          },
          {
            header: 'Status',
            accessor: (user) => (
              <span className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-semibold border ${getStatusBadge(user)}`}>
                {user.is_suspended ? <Ban size={14} /> : user.is_active ? <CheckCircle size={14} /> : <Clock size={14} />}
                <span>{getStatusText(user)}</span>
              </span>
            ),
            align: 'center',
            sortable: true,
          },
          {
            header: 'Verified',
            accessor: (user) => (
              user.is_verified ? (
                <span className="inline-flex items-center space-x-1 text-success-600">
                  <Shield size={16} />
                  <span className="text-xs font-semibold">Verified</span>
                </span>
              ) : (
                <span className="text-xs text-secondary-400">Not Verified</span>
              )
            ),
            align: 'center',
            sortable: true,
          },
          {
            header: 'Total Spent',
            accessor: (user) => (
              <div>
                <p className="font-semibold text-secondary-900">
                  {user.total_spent || '$0.00'}
                </p>
                <p className="text-xs text-secondary-500">{user.total_transactions || 0} transactions</p>
              </div>
            ),
            align: 'center',
            sortable: true,
          },
          {
            header: 'Joined',
            accessor: (user) => (
              <p className="text-sm text-secondary-700">
                {new Date(user.created_at).toLocaleDateString('en-IN', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </p>
            ),
            align: 'center',
            sortable: true,
          },
        ]}
        renderActions={(user) => (
          <div className="flex items-center justify-center space-x-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleViewDetails(user.user_id);
              }}
              className="bg-primary-50 hover:bg-primary-100 text-primary-600 p-2 rounded-lg transition-colors"
              title="View Details"
            >
              <Eye size={18} />
            </button>

            {!user.is_suspended ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSuspendModal({ show: true, user });
                }}
                className="bg-danger-50 hover:bg-danger-100 text-danger-600 p-2 rounded-lg transition-colors"
                title="Suspend User"
              >
                <Ban size={18} />
              </button>
            ) : (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleUnsuspend(user.user_id);
                }}
                className="bg-success-50 hover:bg-success-100 text-success-600 p-2 rounded-lg transition-colors"
                title="Unsuspend User"
              >
                <CheckCircle size={18} />
              </button>
            )}
          </div>
        )}
      />

      {/* Suspend Modal */}
      {suspendModal.show && suspendModal.user && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b">
              <div className="flex items-center space-x-3">
                <div className="bg-danger-100 p-3 rounded-full">
                  <Ban className="text-danger-600" size={24} />
                </div>
                <h2 className="text-xl font-bold text-secondary-900">Suspend User</h2>
              </div>
              <button onClick={() => setSuspendModal({ show: false, user: null })} className="text-secondary-400 hover:text-secondary-600">
                <X size={24} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-secondary-700">
                Suspending <strong>{suspendModal.user.full_name}</strong>
              </p>
              <div>
                <label className="block text-sm font-semibold text-secondary-700 mb-2">
                  Suspension Reason <span className="text-danger-500">*</span>
                </label>
                <textarea
                  value={suspensionReason}
                  onChange={(e) => setSuspensionReason(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                  placeholder="Provide a clear reason for suspension..."
                  rows={4}
                  required
                />
              </div>
            </div>
            <div className="flex space-x-3 p-6 bg-secondary-50 rounded-b-2xl">
              <button
                onClick={() => setSuspendModal({ show: false, user: null })}
                disabled={processing}
                className="flex-1 px-6 py-3 bg-white border-2 border-secondary-300 text-secondary-700 rounded-lg font-semibold hover:bg-secondary-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSuspendUser}
                disabled={processing || !suspensionReason.trim()}
                className="flex-1 px-6 py-3 bg-danger-500 hover:bg-danger-600 text-white rounded-lg font-semibold flex items-center justify-center space-x-2 disabled:opacity-50"
              >
                {processing ? (
                  <>
                    <Loader className="animate-spin" size={20} />
                    <span>Suspending...</span>
                  </>
                ) : (
                  <>
                    <Ban size={20} />
                    <span>Suspend User</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Modal */}
      {bulkDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b">
              <div className="flex items-center space-x-3">
                <div className="bg-danger-100 p-3 rounded-full">
                  <Trash2 className="text-danger-600" size={24} />
                </div>
                <h2 className="text-xl font-bold text-secondary-900">Delete Users</h2>
              </div>
              <button onClick={() => setBulkDeleteModal(false)} className="text-secondary-400 hover:text-secondary-600">
                <X size={24} />
              </button>
            </div>
            <div className="p-6">
              <p className="text-secondary-700 mb-4">
                Are you sure you want to delete the <strong>{selectedUsers.length}</strong> selected users?
              </p>
              <p className="text-sm text-secondary-600">
                This action will mark the selected users as deleted, and they will be suspended on the platform.
              </p>
            </div>
            <div className="flex space-x-3 p-6 bg-secondary-50 rounded-b-2xl">
              <button
                onClick={() => setBulkDeleteModal(false)}
                disabled={processing}
                className="flex-1 px-6 py-3 bg-white border-2 border-secondary-300 text-secondary-700 rounded-lg font-semibold hover:bg-secondary-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkDelete}
                disabled={processing}
                className="flex-1 px-6 py-3 bg-danger-500 hover:bg-danger-600 text-white rounded-lg font-semibold flex items-center justify-center space-x-2 disabled:opacity-50"
              >
                {processing ? (
                  <>
                    <Loader className="animate-spin" size={20} />
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <Trash2 size={20} />
                    <span>Delete Users</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;

